import Spinner from "presentation/components/Shared/Spinner";
import { Column } from "primereact/column";
import {
  DataTable,
  DataTableFilterEvent,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortEvent,
} from "primereact/datatable";
import { Toast } from "primereact/toast";
import { useEffect, useRef, useState } from "react";
import HeaderEmployeeGrid from "./HeaderParameterGrid";
import { ParameterInputFilter } from "domain/models/parameter-input-filter";
import { Parameter } from "domain/entities/parameter";
import { useParameterByFilter } from "presentation/hooks/use-query-parameter";
import { useFilterParameterStore } from "presentation/stores/use-filter-parameter-store";
import { useParameterCreateStore } from "presentation/stores/use-parameter-create-store";
import { useParameterDeleteStore } from "presentation/stores/use-parameter-delete-store";
import { confirmDialog } from "primereact/confirmdialog";

const ParameterGrid: React.FC = () => {
  const { filters, setFilters } = useFilterParameterStore();
  const toast = useRef<Toast>(null);
  const [lazyParams, setLazyParams] = useState({
    sortBy: "type",
    sortOrder: "asc",
    limit: 50,
    page: 1,
    filters: {},
  });

  const {
    data: parameters,
    isLoading,
    isError,
    error,
    refetch,
  } = useParameterByFilter({
    sortBy: lazyParams.sortBy,
    sortOrder: lazyParams.sortOrder,
    page: lazyParams.page,
    limit: lazyParams.limit,
    ...lazyParams.filters,
  } as ParameterInputFilter);

  const confirmAction = (message: string, acceptCallback: () => void) => {
    confirmDialog({
      message: message,
      header: "Confirmação",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Sim",
      rejectLabel: "Não",
      accept: acceptCallback,
    });
  };

  const { parameter, setParameter } = useParameterCreateStore();
  const { parameterToDelete, setParameterToDelete } = useParameterDeleteStore();

  useEffect(() => {
    refetch();
  }, [lazyParams, refetch]);

  useEffect(() => {
    setLazyParams((prev) => ({
      ...prev,
      filters: Object.fromEntries(
        Object.entries(filters)
          .map(([key, value]) => {
            if ("constraints" in value) {
              return [key, value.constraints?.[0]?.value ?? ""];
            }
            return [key, (value as DataTableFilterMetaData)?.value ?? ""];
          })
          .filter(
            ([_, value]) =>
              value !== null && value !== undefined && value !== ""
          )
      ),
    }));
  }, [filters]);

  useEffect(() => {
    refetch();
  }, [lazyParams.filters, refetch]);

  if (isLoading) return <Spinner />;

  if (isError && toast.current) {
    toast.current.show({
      severity: "error",
      summary: "Erro",
      detail: error?.message || "Ocorreu um erro inesperado",
      life: 1500,
    });
  }

  const handleDelete = (rowData: Parameter) => {
    setParameterToDelete(rowData);
  };

  const showActionOptions = (rowData: Parameter) => {
    return (
      <div className="spacing-icons">
        <i
          className="pi pi-pencil cursor-pointer"
          onClick={() => setParameter(rowData)}
        />
        <i
          className="pi pi-trash cursor-pointer"
          onClick={() =>
            confirmAction("Deseja eliminar este parâmetro global?", () =>
              handleDelete(rowData)
            )
          }
        />
        {"  "}
      </div>
    );
  };

  return (
    <div>
      <DataTable
        value={parameters?.data || []}
        lazy
        paginator
        first={(lazyParams.page - 1) * lazyParams.limit}
        rows={lazyParams.limit}
        totalRecords={parameters?.pagination?.count || 0}
        loading={isLoading}
        onPage={(e: DataTablePageEvent) =>
          setLazyParams({
            ...lazyParams,
            page: e.page ? e.page + 1 : 1,
            limit: e.rows,
          })
        }
        sortField={lazyParams.sortBy}
        sortOrder={lazyParams.sortOrder === "asc" ? 1 : -1}
        onSort={(e: DataTableSortEvent) =>
          setLazyParams({
            ...lazyParams,
            sortBy: e.sortField,
            sortOrder:
              e.sortOrder === -1 ? "desc" : e.sortOrder === 1 ? "asc" : "",
          })
        }
        onFilter={(e: DataTableFilterEvent) => setFilters(e.filters)}
        filters={filters}
        size="small"
        resizableColumns
        columnResizeMode="expand"
        header={<HeaderEmployeeGrid />}
        rowsPerPageOptions={[5, 10, 25, 50]}
        paginatorLeft
        dataKey="id"
        globalFilterFields={["type", "value", "query"]}
      >
        <Column
          field="type"
          header="Tipo"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          field="value"
          header="Valor"
          sortable
          filter
          showFilterMatchModes={false}
        />
        <Column
          body={showActionOptions}
          header="Ações"
          align="center"
          alignHeader="center"
        />
      </DataTable>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default ParameterGrid;
